import React from 'react';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import {
  DirectionsCar,
  Gavel,
  Groups,
  LocalPolice,
  Memory,
  Pets,
  Psychology,
  Shield,
  SportsMartialArts,
  Traffic,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  HOMEPAGE_TOPIC_CATEGORIES,
  buildHomepageTopicPath,
  type HomepageTopicCategory,
} from '../../../shared/homepage/searchFirstDefaults';
import {
  HOMEPAGE_CARD_RADIUS,
  HOMEPAGE_CARD_SHADOW,
  HOMEPAGE_CARD_SHADOW_HOVER,
  HOMEPAGE_SECTION_PY,
} from '../../../shared/homepage/homepageLayout';
import { trackEvent } from '../../utils/analytics';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';

export type BrowseLink = {
  id: string;
  label: string;
  href: string;
  open_new_tab?: boolean;
};

export type BrowseCategoriesSectionProps = {
  heading?: string;
  /** When CMS explore-bar provides custom links, use those; otherwise topic cards. */
  links?: BrowseLink[];
  useTopicDefaults?: boolean;
};

const iconFor = (key: HomepageTopicCategory['iconKey']) => {
  const sx = { fontSize: 32 };
  switch (key) {
    case 'patrol':
      return <LocalPolice sx={sx} />;
    case 'firearms':
      return <Gavel sx={sx} />;
    case 'traffic':
      return <Traffic sx={sx} />;
    case 'crisis':
      return <Psychology sx={sx} />;
    case 'leadership':
      return <Groups sx={sx} />;
    case 'investigations':
      return <Shield sx={sx} />;
    case 'defensive':
      return <SportsMartialArts sx={sx} />;
    case 'technology':
      return <Memory sx={sx} />;
    case 'evoc':
      return <DirectionsCar sx={sx} />;
    case 'k9':
      return <Pets sx={sx} />;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
};

const BrowseCategoriesSection: React.FC<BrowseCategoriesSectionProps> = ({
  heading = 'Browse by Category',
  links,
  useTopicDefaults = true,
}) => {
  const hasCustomLinks = Array.isArray(links) && links.length > 0 && !useTopicDefaults;

  return (
    <Box
      sx={{
        py: HOMEPAGE_SECTION_PY.browse,
        bgcolor: 'background.default',
        px: AGENCY_SHELL_HORIZONTAL_PX,
      }}
    >
      <Container maxWidth="lg" disableGutters>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            mb: 4,
            textAlign: 'center',
            letterSpacing: '-0.02em',
          }}
        >
          {heading}
        </Typography>

        {hasCustomLinks ? (
          <Grid container spacing={2}>
            {links!.map((link) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={link.id}>
                <Paper
                  component={link.open_new_tab ? 'a' : RouterLink}
                  {...(link.open_new_tab
                    ? { href: link.href, target: '_blank', rel: 'noopener noreferrer' }
                    : { to: link.href })}
                  elevation={0}
                  onClick={() =>
                    trackEvent({
                      action: 'homepage_browse_category_click',
                      category: 'engagement',
                      label: link.label,
                      custom_parameters: { href: link.href },
                    })
                  }
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    p: 2.5,
                    minHeight: 88,
                    borderRadius: HOMEPAGE_CARD_RADIUS,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: HOMEPAGE_CARD_SHADOW,
                    textAlign: 'center',
                    fontWeight: 600,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: HOMEPAGE_CARD_SHADOW_HOVER,
                      borderColor: 'primary.light',
                    },
                  }}
                >
                  {link.label}
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {HOMEPAGE_TOPIC_CATEGORIES.map((topic) => {
              const href = buildHomepageTopicPath(topic);
              return (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={topic.id}>
                  <Paper
                    component={RouterLink}
                    to={href}
                    elevation={0}
                    onClick={() =>
                      trackEvent({
                        action: 'homepage_browse_category_click',
                        category: 'engagement',
                        label: topic.label,
                        custom_parameters: { href, search: topic.search },
                      })
                    }
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.25,
                      textDecoration: 'none',
                      color: 'inherit',
                      p: 2.5,
                      minHeight: 120,
                      borderRadius: HOMEPAGE_CARD_RADIUS,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      boxShadow: HOMEPAGE_CARD_SHADOW,
                      textAlign: 'center',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: HOMEPAGE_CARD_SHADOW_HOVER,
                        borderColor: 'primary.light',
                        '& .topic-icon': { color: 'primary.main' },
                      },
                    }}
                  >
                    <Box className="topic-icon" sx={{ color: 'text.secondary' }}>
                      {iconFor(topic.iconKey)}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {topic.label}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default BrowseCategoriesSection;
